import React from "react";
import { CustomAutoComplete } from "components/CustomComponents/CustomAutoComplete";
import { CustomTextBox } from "components/CustomComponents";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import dayjs from "dayjs";

// Pay Inputs screens use the shared CustomAutoComplete / CustomTextBox
// inputs. These wrappers only adapt their props to plain
// value / onChange(value) with { value, label } options, so the screens
// don't each convert to and from option objects.

export const SelectField = ({
  value,
  onChange,
  options,
  placeholder,
  error,
  disabled,
  sx,
  // Pass false inside a scrolling container (e.g. a table) so the list
  // isn't clipped by it.
  disablePortal = true,
}) => (
  <CustomAutoComplete
    dataList={options}
    selectedValue={options.find((option) => option.value === value) ?? null}
    updateDataFunction={(event, option) => onChange(option?.value ?? "")}
    placeHolder={placeholder}
    textBoxError={Boolean(error)}
    disableAction={disabled}
    sx={sx}
    disablePortal={disablePortal}
  />
);

export const TextField = ({ value, onChange, type, placeholder, error, disabled }) => (
  <CustomTextBox
    type={type}
    selectedValue={value}
    updateDataFunction={(event) => onChange(event.target.value)}
    placeHolder={placeholder}
    textBoxError={Boolean(error)}
    disabledValue={disabled}
  />
);

// The calendar date picker used on the redesigned screens (same picker and
// settings as CustomizedDatePicker, e.g. Team Workspace Approvals' Apply
// panels) for plain-state forms. value / onChange use "YYYY-MM-DD" strings;
// minDate / maxDate ("YYYY-MM-DD") grey out the days outside them.
// No DemoContainer: it adds 8px of top padding, which pushed the picker
// below the input next to it in two-column forms.
export const DateField = ({ value, onChange, minDate, maxDate, placeholder = "DD-MM-YYYY", error, disabled }) => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <DatePicker
      value={value ? dayjs(value) : null}
      onChange={(next) => onChange(next && next.isValid() ? next.format("YYYY-MM-DD") : "")}
      format="DD-MM-YYYY"
      minDate={minDate ? dayjs(minDate) : undefined}
      maxDate={maxDate ? dayjs(maxDate) : undefined}
      disabled={disabled}
      slotProps={{
        textField: { size: "small", fullWidth: true, placeholder, error: Boolean(error) },
      }}
      sx={{ width: "100%" }}
    />
  </LocalizationProvider>
);

// The 24-hour clock picker used for durations on other screens (same
// TimePicker setup as CustomTimePicker, e.g. My Zone > Overtime "Duration
// Hrs") for plain-state forms. value / onChange use "HH:MM" strings; a decimal
// hours value (e.g. "2.5") is shown as 02:30.
const toClock = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return null;
  let hours;
  let minutes;
  // "HH:MM" (or "HH:MM:SS" from the server; seconds are ignored).
  if (/^\d{1,2}:[0-5]\d(:[0-5]\d)?$/.test(text)) [hours, minutes] = text.split(":").map(Number);
  else if (Number.isFinite(Number(text))) {
    const total = Math.round(Number(text) * 60);
    hours = Math.floor(total / 60);
    minutes = total % 60;
  } else return null;
  if (hours > 23) return null;
  return dayjs().startOf("day").hour(hours).minute(minutes);
};

export const TimeField = ({ value, onChange, error, disabled }) => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <TimePicker
      ampm={false}
      format="HH:mm"
      viewRenderers={{ hours: renderTimeViewClock, minutes: renderTimeViewClock }}
      views={["hours", "minutes"]}
      openTo="hours"
      value={toClock(value)}
      onChange={(next) => onChange(next && next.isValid() ? next.format("HH:mm") : "")}
      disabled={disabled}
      slotProps={{ textField: { size: "small", fullWidth: true, placeholder: "HH:MM", error: Boolean(error) } }}
      sx={{ width: "100%" }}
    />
  </LocalizationProvider>
);

export const toOptions = (values) => values.map((value) => ({ value, label: value }));
